<template>
  <div ref="mainContainerRef" class="tw-relative tw-flex tw-flex-col tw-gap-3">
    <div class="tw-flex tw-flex-wrap tw-gap-2 tw-max-h-[28px]">
      <label id="label" class="tw-text-body-bold-mobile apTablet2:tw-text-body-bold-desktop">{{ title }}</label>
      <p v-if="props.isOptional" class="tw-text-body-mobile apTablet2:tw-text-body-desktop">
        (optional)
      </p>
    </div>

    <div class="tw-flex tw-gap-4">
      <!-- Country Code -->
      <Dropdown
        ref="dropdownManual"
        :manual="true"
        :border="false"
        :align="isMobile ? 'right' : 'left'"
        @visible-change="onVisibleChange"
      >
        <template #trigger>
          <div class="tw-bg-white  apDesktop:tw-rounded-xl tw-rounded-[10px]">
            <input
              id="country-code"
              ref="countryCodeInputRef"
              v-model="countryCodeRef"
              :class="[
                // 'dark:tw-text-white dark:tw-outline-ap-night-rider placeholder:dark:tw-text-white dark:tw-bg-ap-nero',
                'tw-text-body-md-mobile apTablet2:tw-text-body-md-desktop',
                'tw-w-full apDesktop:tw-h-16 tw-h-12 apDesktop:tw-rounded-xl tw-rounded-[10px] tw-bg-white tw-text-gray-900 tw-text-lg tw-px-6 tw-py-3',
                'tw-outline tw-outline-ap-gainsboro  tw-outline-offset-[-2px] tw-outline-1',
                'focus:tw-outline-ap-dodger-blue focus:tw-outline-2',
                'hover:tw-outline-ap-dodger-blue hover:tw-outline-2',
              ]"
              :placeholder="tempCountryCode"
              autocomplete="new-password"
              autocorrect="off"
              spellcheck="false"
              :style="{
                background: `url(${isDarkMode ? $cdn('icons/contact-us/icon_caret_down.svg') : $cdn('icons/contact-us/icon_caret_down.svg')})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '90% center',
              }"
              @click="onTapCountryCodeDropdown"
              @focus="onTapCountryCodeDropdown"
              @focusout="onFocusoutCountryCodeDropdown"
              @input="onInputCountryCode"
            >
          </div>
        </template>
        <!-- Selection -->
        <div
          :class="[
            'tw-max-h-[250px] tw-w-full tw-overflow-y-auto tw-p-2 tw-bg-white tw-shadow-lg tw-rounded-xl tw-flex tw-flex-col'
          ]"
          :style="{
            minWidth: menuWidth + 'px',
          }"
        >
          <div v-for="(option, index) in filteredCountryCodeOptions" :key="option.code">
            <button
              type="button"
              class="tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-lg hover:tw-bg-blue-500 hover:tw-text-white tw-rounded-md"
              :class="{
                'tw-bg-blue-500 tw-text-white': dropdownSelectIndex === index,
              }"
              @pointerdown="onSelectCountryCode(option.dial_code)"
            >
              <div class="tw-flex tw-justify-between tw-items-center">
                <span class="">{{ option.name }}</span>
                <span class="">{{ option.dial_code }}</span>
              </div>
            </button>
          </div>
        </div>
      </Dropdown>

      <!-- Phone Number -->
      <input
        id="role"
        v-model="phoneNumberRef"
        type="text"
        :class="[
          // 'dark:tw-text-white dark:tw-outline-ap-night-rider dark:tw-bg-ap-nero',
          'placeholder:dark:tw-text-nobel',
          'tw-w-full apDesktop:tw-h-16 tw-h-12 tw-rounded-xl tw-bg-white  tw-text-gray-900 tw-px-6 tw-py-3',
          'tw-outline tw-outline-ap-gainsboro  tw-outline-offset-[-2px] tw-outline-1',
          'apTablet2:tw-text-body-md-desktop tw-text-body-md-mobile',
          'focus:tw-outline-ap-dodger-blue focus:tw-outline-2',
        ]"
        placeholder="1234567890"
        @input="onPhoneNumberChange"
      >
    </div>
    <span
      :class="[
        'tw-absolute tw-bottom-[-32px] tw-left-0 tw-text-red-500 tw-text-base tw-mt-2',
        'tw-transition-opacity tw-duration-300',
        { '!tw-opacity-0': validPhoneNumber }
      ]"
    >
      Invalid phone number.
    </span>
  </div>
</template>


<script setup lang="ts">
import { gsap } from 'gsap';
import Dropdown from 'v-dropdown';
import { computed, onMounted, ref, watch } from 'vue';
import CountryCodeJSON from '../../../assets/json/country-dial-codes.json';
import { getDarkMode } from '../../support/helper';

const isDarkMode = ref(false);

const dropdownManual = ref(null);
const dropdownSelectIndex = ref(0);
const tempCountryCode = ref('+65');

const mainContainerRef = ref(null);

const phoneNumberRef = ref('');
const countryCodeRef = ref('+65');
const countryCodeInputRef = ref(null);

// const CountryCodeJSON = ref<CountryCode[]>([]);


const emit = defineEmits(['phoneNumberValid', 'update:phoneNumber', 'update:countryCode']);

interface CountryCode {
  name: string;
  dial_code: string;
  code: string;
}

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  formContainer: {
    type: Object,
    default: undefined
  },
  countryCode: {
    type: String,
    required: true,
  },
  phoneNumber: {
    type: String,
    required: true
  },
  isOptional: {
    type: Boolean,
    default: false
  }
});

// Computed value for filtering country codes
const filteredCountryCodeOptions = computed(() => {
  if (dropdownManual.value) {
    (dropdownManual.value as any).adjust();
  }
  dropdownSelectIndex.value = 0;
  if (countryCodeRef.value.length === 0) {
    return CountryCodeJSON;
  }
  return CountryCodeJSON.filter((option: CountryCode) => {
    return option.name.toLowerCase().includes(countryCodeRef.value.toLowerCase()) ||
      option.dial_code.toLowerCase().includes(countryCodeRef.value.toLowerCase());
  });
});

const isMobile = computed(() => {
  return window.innerWidth <= 832;
});

const dropdownStyle = ref({
  width: '100%',
});

// Function to update the phone number input to only allow numeric values
function onPhoneNumberChange(event: any) {
  const inputElement = event.target;
  let inputValue = inputElement.value;

  // Extract only numeric characters
  const numericValue = inputValue.replace(/\D/g, '');
  inputElement.value = numericValue;

  // Set cursor to the end of the input value
  if (typeof inputElement.selectionStart === 'number') {
    inputElement.selectionStart = inputElement.selectionEnd = numericValue.length;
    emit('update:phoneNumber', numericValue);
  } else {
    inputElement.setSelectionRange(numericValue.length, numericValue.length);
  }
}

function onInputCountryCode(_: any) {
  if (dropdownManual.value) {
    (dropdownManual.value as any).display();
    setTimeout(() => {
      (dropdownManual.value as any).adjust();
    }, 100);
  }
}

// Computed value to validate the phone number
const validPhoneNumber = computed(() => {
  if (phoneNumberRef.value.length > 0) {
    let minimumPhoneNumberLength = 8;
    if (countryCodeRef.value !== '+60') {
      minimumPhoneNumberLength = 6;
    }
    const phoneNumberRegex = new RegExp(`^\\d{${minimumPhoneNumberLength},}$`);
    return phoneNumberRegex.test(phoneNumberRef.value.trim());
  }
  return true;
});

// Function to display the country code dropdown
function onTapCountryCodeDropdown() {
  if (dropdownManual.value) {
    countryCodeRef.value = '';
    if (isMobile.value) {
      if (props.formContainer) {
        gsap.to(props.formContainer, {
          scrollTop: (mainContainerRef.value as any).offsetTop,
          duration: 0.2,
        });
        setTimeout(() => {
          (dropdownManual.value as any).display();
          setTimeout(() => {
            (dropdownManual.value as any).adjust();
          }, 200);
        }, 300);
      }
    } else {
      (dropdownManual.value as any).display();
      setTimeout(() => {
        (dropdownManual.value as any).adjust();
      }, 200);
    }
  }
}

// Function to select a country code from the dropdown
function onSelectCountryCode(dialCode: string) {
  if (dropdownManual.value) {
    if (dialCode.length > 0) {
      countryCodeRef.value = dialCode;
      tempCountryCode.value = dialCode;
      setTimeout(() => {
        (dropdownManual.value as any).close();
      }, 200);
    }
  }
}

// Function to handle focusout event of the country code dropdown
function onFocusoutCountryCodeDropdown(_: FocusEvent) {
  window.removeEventListener('keydown', handleKeyEvent);
  setTimeout(() => {
    countryCodeRef.value = tempCountryCode.value;
    if (dropdownManual.value) {
      (dropdownManual.value as any).close();
    }
  }, 200);
}

// Function to scroll to the active item in the dropdown
const scrollToActiveItem = (key: any) => {
  const dropdown = document.querySelector('.country-code-dropdown-content');
  const activeItem = document.querySelector('.country-code-dropdown-item.active');

  if (dropdown && activeItem) {
    const dropdownRect = dropdown.getBoundingClientRect();
    const activeRect = activeItem.getBoundingClientRect();

    const difference = activeRect.top - dropdownRect.top;
    if (difference > 128 && key === 'ArrowDown') {
      dropdown.scrollTop += 40;
    } else if (difference < 40 && key === 'ArrowUp') {
      dropdown.scrollTop -= 40;
    }
  }
};
// Function to handle keyboard events for the country code dropdown
function handleKeyEvent(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
      if (dropdownSelectIndex.value === filteredCountryCodeOptions.value.length - 1) {
        return;
      }
      dropdownSelectIndex.value = (dropdownSelectIndex.value + 1) % filteredCountryCodeOptions.value.length;
      scrollToActiveItem(event.key);
      break;
    case 'ArrowUp':
      if (dropdownSelectIndex.value === 0) {
        return;
      }
      dropdownSelectIndex.value = (dropdownSelectIndex.value - 1 + filteredCountryCodeOptions.value.length) % filteredCountryCodeOptions.value.length;
      scrollToActiveItem(event.key);
      break;
    case 'Enter':
      event.preventDefault();
      onSelectCountryCode(filteredCountryCodeOptions.value[dropdownSelectIndex.value].dial_code);
      break;
  }
}

function onVisibleChange(visible: boolean) {
  if (visible) {
    if (props.formContainer) {
      props.formContainer.addEventListener('scroll', handleScroll);
      window.addEventListener('keydown', handleKeyEvent);
    }
  } else {
    if (props.formContainer) {
      props.formContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyEvent);
    }
  }
}

function handleScroll() {
  if (countryCodeInputRef.value) {
    (countryCodeInputRef.value as any).blur();
  }
}

const menuWidth = ref(500);

onMounted(() => {
  isDarkMode.value = getDarkMode();
  // Function to find element with class .v-dropdown-container
  // change the element background-color to #242424
  const dropdownContainer = document.querySelector('.v-dropdown-container');
  if (dropdownContainer) {
    dropdownContainer.classList.add('tw-bg-ap-nero');
  }

  watch(validPhoneNumber, () => {
    emit('phoneNumberValid', validPhoneNumber.value);
  });

  watch(countryCodeRef, () => {
    emit('update:countryCode', countryCodeRef.value);
  });

  watch(phoneNumberRef, () => {
    emit('update:phoneNumber', phoneNumberRef.value);
  });

  const label = document.getElementById('label');
  if (label) {
    menuWidth.value = label.offsetWidth;
  }

  phoneNumberRef.value = props.phoneNumber;
  countryCodeRef.value = props.countryCode;
  dropdownStyle.value = {
    width: (mainContainerRef.value as any).offsetWidth + 'px',
  };
});
</script>